/**
 * PulizieList Component - Hedita Design System
 * Feature: Activities - Lista attività pulizie
 */

import { useState } from "react";
import { Plus, Search, Sparkles, CalendarDays, CheckCircle2, Clock, Home } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { useActivities, useActivityStats, useDeleteActivity, useUpdateActivity } from "../hooks";
import { ActivityForm } from "./AttivitaForm";
import type { Activity, ActivityStatus } from "../types";

const STATI: ActivityStatus[] = ["da_fare", "in_corso", "completata", "annullata"];

const STATO_CONFIG: Record<ActivityStatus, { bg: string; color: string; border: string; label: string; icon: React.ReactNode }> = {
  "da_fare": { 
    bg: "bg-amber-500/10", color: "text-amber-400", border: "border-amber-500/20", label: "Da fare",
    icon: <Clock className="h-3.5 w-3.5" />
  },
  "in_corso": { 
    bg: "bg-sky-500/10", color: "text-sky-400", border: "border-sky-500/20", label: "In corso",
    icon: <Clock className="h-3.5 w-3.5" />
  },
  "completata": { 
    bg: "bg-emerald-500/10", color: "text-emerald-400", border: "border-emerald-500/20", label: "Completata",
    icon: <CheckCircle2 className="h-3.5 w-3.5" />
  },
  "annullata": { 
    bg: "bg-muted/30", color: "text-muted-foreground", border: "border-border", label: "Annullata",
    icon: <Clock className="h-3.5 w-3.5" />
  },
};

export function PulizieList() {
  const [search, setSearch] = useState("");
  const [statoFilter, setStatoFilter] = useState<ActivityStatus | null>("da_fare");
  const [formOpen, setFormOpen] = useState(false);
  const [editId, setEditId] = useState<string | undefined>();
  
  const { data: activities = [], isLoading } = useActivities({ tipo: "Pulizie" });
  const { data: stats } = useActivityStats();
  const updateActivity = useUpdateActivity();
  const deleteActivity = useDeleteActivity();

  const filtered = activities.filter(a => {
    const matchSearch = !search || a.titolo?.toLowerCase().includes(search.toLowerCase());
    const matchStato = !statoFilter || a.stato === statoFilter;
    return matchSearch && matchStato;
  });

  const handleStatoChange = (id: string, stato: ActivityStatus) => {
    updateActivity.mutate({ id, data: { stato } });
  };

  const handleEdit = (id: string) => {
    setEditId(id);
    setFormOpen(true);
  };

  const handleNew = () => {
    setEditId(undefined);
    setFormOpen(true);
  };

  const handleFormClose = () => {
    setFormOpen(false);
    setEditId(undefined);
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Skeleton className="h-7 w-32" />
          <Skeleton className="h-10 w-36" />
        </div>
        <div className="grid grid-cols-4 gap-3">
          {[1,2,3,4].map(i => <Skeleton key={i} className="h-24 rounded-xl" />)}
        </div>
        <Skeleton className="h-96 rounded-2xl" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-foreground flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            Pulizie
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">Gestione attività di pulizia</p>
        </div>
        <button 
          onClick={handleNew}
          className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2.5 rounded-xl text-sm font-medium hover:bg-primary/90 transition-all shadow-sm"
        >
          <Plus className="h-4 w-4" /> Nuova pulizia
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {STATI.map(s => {
          const cfg = STATO_CONFIG[s];
          const count = activities.filter(a => a.stato === s).length;
          const active = statoFilter === s;
          
          return (
            <button
              key={s}
              onClick={() => setStatoFilter(active ? null : s)}
              className={`rounded-xl border p-4 text-center transition-all ${
                active ? `${cfg.bg} ${cfg.border} ring-1 ring-primary/20` : "border-border bg-card hover:border-primary/20"
              }`}
            >
              <div className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-lg mb-2 ${cfg.bg} ${cfg.color} ${cfg.border}`}>
                {cfg.icon}
                <span>{cfg.label}</span>
              </div>
              <div className={`text-2xl font-bold ${active ? cfg.color : 'text-foreground'}`}>
                {count}
              </div>
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Cerca attività..."
          className="w-full pl-10 pr-3 py-2.5 rounded-xl border border-border bg-card text-sm focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/20 transition-all"
        />
      </div>

      {/* Lista */}
      <div className="bg-card border border-border rounded-2xl overflow-hidden">
        {filtered.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-16 h-16 rounded-2xl bg-secondary/50 border border-border/50 flex items-center justify-center mx-auto mb-4">
              <Sparkles className="h-8 w-8 text-muted-foreground/40" />
            </div>
            <p className="font-medium text-foreground mb-1">Nessuna attività</p>
            <p className="text-sm text-muted-foreground mb-4">Crea la prima attività di pulizia</p>
            <button 
              onClick={handleNew}
              className="px-4 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-all"
            >
              Nuova pulizia
            </button>
          </div>
        ) : (
          <div className="divide-y divide-border/50">
            {filtered.map((a: Activity) => {
              const stato = STATO_CONFIG[a.stato];
              
              return (
                <div key={a.id} className="p-4 hover:bg-secondary/30 transition-all">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-2">
                        <span className={`px-2 py-0.5 rounded-lg text-xs font-medium border flex items-center gap-1.5 ${stato.bg} ${stato.color} ${stato.border}`}>
                          {stato.icon}
                          {stato.label}
                        </span>
                        {a.struttura_nome && (
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            <Home className="h-3 w-3" />
                            {a.struttura_nome}
                          </span>
                        )}
                      </div>
                      <h3 className="font-semibold text-foreground text-sm mb-1">{a.titolo}</h3>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <CalendarDays className="h-3.5 w-3.5" />
                        <span>{a.data ? new Date(a.data).toLocaleDateString("it-IT") : "—"}</span>
                        {a.ora_inizio && <span>{a.ora_inizio}</span>}
                      </div>
                      {a.descrizione && (
                        <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{a.descrizione}</p>
                      )}
                    </div>
                    
                    {/* Quick Actions */}
                    <div className="flex flex-col gap-1 flex-shrink-0">
                      {a.stato !== "completata" && a.stato !== "annullata" && (
                        <button
                          onClick={() => handleStatoChange(a.id, a.stato === "da_fare" ? "in_corso" : "completata")}
                          className="p-2 rounded-lg bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 transition-colors"
                          title="Completa"
                        >
                          <CheckCircle2 className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        onClick={() => handleEdit(a.id)}
                        className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary/50 transition-colors"
                        title="Modifica"
                      >
                        <span className="text-xs">Modifica</span>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Form Drawer */}
      <ActivityForm
        id={editId}
        open={formOpen}
        onOpenChange={handleFormClose}
        defaultTipo="Pulizie"
        onSaved={() => {}}
      />
    </div>
  );
}
